// RoomPulse — real "booked 3 h ago · 4 guests viewed today" chips for website room cards (GET /api/public/booking-pulse).
import React, { useEffect, useState } from 'react';
import { Eye, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const cache = {};

export const usePulse = () => {
  const { lang } = useLanguage();
  const [data, setData] = useState(cache[lang] || null);
  useEffect(() => {
    if (cache[lang]) { setData(cache[lang]); return undefined; }
    let alive = true;
    fetch(`${API}/api/public/booking-pulse?lang=${lang}`).then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { cache[lang] = d; if (alive) setData(d); } }).catch(() => {});
    return () => { alive = false; };
  }, [lang]);
  return data;
};

export const visitorId = () => {
  try {
    let v = localStorage.getItem('lrn_vid');
    if (!v || !/^[a-z0-9]{8,40}$/.test(v)) {
      v = (Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/[^a-z0-9]/g, '').slice(0, 24);
      localStorage.setItem('lrn_vid', v);
    }
    return v;
  } catch (e) { return `anon${Date.now().toString(36)}`; }
};

export const recordRoomView = (siteRoomId) => {
  fetch(`${API}/api/public/pulse/view`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
    body: JSON.stringify({ vid: visitorId(), site_rooms: [Number(siteRoomId)], page: 'site' }) }).catch(() => {});
};

const TONE = { booked: 'bg-emerald-50 text-emerald-800 border-emerald-200', week: 'bg-orange-50 text-orange-800 border-orange-200', views: 'bg-blue-50 text-blue-900 border-blue-200' };

const stockCache = {};
// "Only N rooms of this type left for your dates" in the guest's language, same source/rules as the Beds24 page (1–3 units, live).
export const useStockLines = (checkIn, checkOut, adults = 2) => {
  const { lang } = useLanguage();
  const key = checkIn && checkOut ? `${lang}|${checkIn}|${checkOut}|${adults}` : '';
  const [stock, setStock] = useState(key ? stockCache[key] || null : null);
  useEffect(() => {
    if (!key) { setStock(null); return undefined; }
    if (stockCache[key]) { setStock(stockCache[key]); return undefined; }
    let alive = true;
    fetch(`${API}/api/public/booking-pulse?lang=${lang}&checkin=${checkIn}&checkout=${checkOut}&adults=${adults}`).then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { stockCache[key] = d.stock || {}; if (alive) setStock(stockCache[key]); } }).catch(() => {});
    return () => { alive = false; };
  }, [key, lang, checkIn, checkOut, adults]);
  return stock || {};
};

export const RoomPulse = ({ roomId, className = '' }) => {
  const d = usePulse();
  const lines = d?.site_rooms?.[String(roomId)]?.lines || [];
  if (!lines.length) return null;
  const chips = lines.map((l) => {
    const Icon = l.k === 'views' ? Eye : Zap;
    return <span key={l.k} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${TONE[l.k] || TONE.booked}`} data-testid={`room-pulse-${l.k}-${roomId}`}><Icon size={11} />{l.text}</span>;
  });
  return <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid={`room-pulse-${roomId}`}>{chips}</div>;
};
